import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * Theme definitions.
 *
 * IMPORTANT: every Tailwind class below must appear here as a complete literal
 * string, including its variant prefix (`hover:`, `aria-selected:`). Tailwind
 * generates CSS by scanning source files for class-shaped text, so a class
 * assembled at runtime — `hover:${gradient.buttonHover}` or
 * `aria-selected:${x.split(' ')[0]}` — is never emitted into the stylesheet and
 * silently does nothing. Spell the variants out; don't interpolate them.
 */
export const themes = {
  oceanBlue: {
    name: 'Ocean Blue',
    description: 'Classic finance feel',
    colors: {
      primary: {
        50: '#eff6ff',
        100: '#dbeafe',
        200: '#bfdbfe',
        300: '#93c5fd',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
        700: '#1d4ed8',
      },
      accent: {
        50: '#f0fdfa',
        100: '#ccfbf1',
        400: '#2dd4bf',
        500: '#14b8a6',
        600: '#0d9488',
      },
      gradient: {
        from: 'from-blue-500',
        via: 'via-blue-600',
        to: 'to-cyan-600',
        bg: 'from-blue-600 via-blue-700 to-cyan-700',
        button: 'from-blue-500 to-cyan-500',
        buttonHover: 'hover:from-blue-600 hover:to-cyan-600',
        navSelected: 'aria-selected:from-blue-500 aria-selected:to-cyan-500 aria-selected:shadow-blue-200',
      },
      shadow: 'shadow-blue-200',
      ring: 'ring-blue-500',
      text: {
        muted: 'text-blue-300',
        accent: 'text-cyan-400',
      },
    },
  },
  emeraldGreen: {
    name: 'Emerald Green',
    description: 'Growth-oriented, Mint style',
    colors: {
      primary: {
        50: '#ecfdf5',
        100: '#d1fae5',
        200: '#a7f3d0',
        300: '#6ee7b7',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
        700: '#047857',
      },
      accent: {
        50: '#f0fdf4',
        100: '#dcfce7',
        400: '#4ade80',
        500: '#22c55e',
        600: '#16a34a',
      },
      gradient: {
        from: 'from-emerald-500',
        via: 'via-emerald-600',
        to: 'to-teal-600',
        bg: 'from-emerald-600 via-emerald-700 to-teal-700',
        button: 'from-emerald-500 to-teal-500',
        buttonHover: 'hover:from-emerald-600 hover:to-teal-600',
        navSelected: 'aria-selected:from-emerald-500 aria-selected:to-teal-500 aria-selected:shadow-emerald-200',
      },
      shadow: 'shadow-emerald-200',
      ring: 'ring-emerald-500',
      text: {
        muted: 'text-emerald-300',
        accent: 'text-teal-400',
      },
    },
  },
  slateCoral: {
    name: 'Slate & Coral',
    description: 'Sophisticated neutral with warm accents',
    colors: {
      primary: {
        50: '#f8fafc',
        100: '#f1f5f9',
        200: '#e2e8f0',
        300: '#cbd5e1',
        400: '#94a3b8',
        500: '#64748b',
        600: '#475569',
        700: '#334155',
      },
      accent: {
        50: '#fff7ed',
        100: '#ffedd5',
        400: '#fb923c',
        500: '#f97316',
        600: '#ea580c',
      },
      gradient: {
        from: 'from-slate-600',
        via: 'via-slate-700',
        to: 'to-slate-800',
        bg: 'from-slate-700 via-slate-800 to-slate-900',
        button: 'from-orange-400 to-rose-400',
        buttonHover: 'hover:from-orange-500 hover:to-rose-500',
        navSelected: 'aria-selected:from-orange-400 aria-selected:to-rose-400 aria-selected:shadow-slate-300',
      },
      shadow: 'shadow-slate-300',
      ring: 'ring-slate-500',
      text: {
        muted: 'text-slate-400',
        accent: 'text-orange-400',
      },
    },
  },
  warmIndigo: {
    name: 'Warm Indigo',
    description: 'Refined indigo without purple',
    colors: {
      primary: {
        50: '#eef2ff',
        100: '#e0e7ff',
        200: '#c7d2fe',
        300: '#a5b4fc',
        400: '#818cf8',
        500: '#6366f1',
        600: '#4f46e5',
        700: '#4338ca',
      },
      accent: {
        50: '#ecfdf5',
        100: '#d1fae5',
        400: '#34d399',
        500: '#10b981',
        600: '#059669',
      },
      gradient: {
        from: 'from-indigo-500',
        via: 'via-indigo-600',
        to: 'to-indigo-700',
        bg: 'from-indigo-600 via-indigo-700 to-indigo-800',
        button: 'from-indigo-500 to-indigo-600',
        buttonHover: 'hover:from-indigo-600 hover:to-indigo-700',
        navSelected: 'aria-selected:from-indigo-500 aria-selected:to-indigo-600 aria-selected:shadow-indigo-200',
      },
      shadow: 'shadow-indigo-200',
      ring: 'ring-indigo-500',
      text: {
        muted: 'text-indigo-300',
        accent: 'text-indigo-400',
      },
    },
  },
  forestGold: {
    name: 'Forest & Gold',
    description: 'Premium wealth management feel',
    colors: {
      primary: {
        50: '#f0fdf4',
        100: '#dcfce7',
        200: '#bbf7d0',
        300: '#86efac',
        400: '#4ade80',
        500: '#22c55e',
        600: '#166534',
        700: '#14532d',
      },
      accent: {
        50: '#fefce8',
        100: '#fef9c3',
        400: '#facc15',
        500: '#eab308',
        600: '#ca8a04',
      },
      gradient: {
        from: 'from-green-700',
        via: 'via-green-800',
        to: 'to-emerald-900',
        bg: 'from-green-800 via-green-900 to-emerald-950',
        button: 'from-amber-400 to-yellow-500',
        buttonHover: 'hover:from-amber-500 hover:to-yellow-600',
        navSelected: 'aria-selected:from-amber-400 aria-selected:to-yellow-500 aria-selected:shadow-green-200',
      },
      shadow: 'shadow-green-200',
      ring: 'ring-green-600',
      text: {
        muted: 'text-green-300',
        accent: 'text-amber-400',
      },
    },
  },
  monoBlue: {
    name: 'Monochrome Blue',
    description: 'Apple-like minimalism',
    colors: {
      primary: {
        50: '#f9fafb',
        100: '#f3f4f6',
        200: '#e5e7eb',
        300: '#d1d5db',
        400: '#9ca3af',
        500: '#6b7280',
        600: '#4b5563',
        700: '#374151',
      },
      accent: {
        50: '#eff6ff',
        100: '#dbeafe',
        400: '#60a5fa',
        500: '#3b82f6',
        600: '#2563eb',
      },
      gradient: {
        from: 'from-gray-700',
        via: 'via-gray-800',
        to: 'to-gray-900',
        bg: 'from-gray-800 via-gray-900 to-black',
        button: 'from-blue-500 to-blue-600',
        buttonHover: 'hover:from-blue-600 hover:to-blue-700',
        navSelected: 'aria-selected:from-blue-500 aria-selected:to-blue-600 aria-selected:shadow-gray-300',
      },
      shadow: 'shadow-gray-300',
      ring: 'ring-blue-500',
      text: {
        muted: 'text-gray-400',
        accent: 'text-blue-400',
      },
    },
  },
};

const DEFAULT_THEME = 'warmIndigo';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [themeName, setThemeName] = useState(() => {
    try {
      const saved = localStorage.getItem('app-theme');
      return saved && themes[saved] ? saved : DEFAULT_THEME;
    } catch (error) {
      // Safari private browsing and similar can throw on localStorage access.
      return DEFAULT_THEME;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('app-theme', themeName);
    } catch (error) {
      // Persistence is a nicety; a failure here shouldn't break the app.
    }
  }, [themeName]);

  const theme = themes[themeName] ?? themes[DEFAULT_THEME];

  const value = {
    themeName,
    setThemeName,
    theme,
    themes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
